/**
 * ResourceFlowGraph — SVG dependency visualization for preflight items.
 *
 * Renders a directed acyclic graph (DAG) where:
 *   - Each node is a preflight item (sized by priority, colored by state)
 *   - Edges connect depends_on relationships (source blocks target)
 *   - Nodes without dependencies are placed at the top
 *   - Topological sort determines vertical layers
 *
 * Clicking a node fires onSelectItem for detail view.
 */

import React, { useMemo, useCallback, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { PreflightItem, PreflightState, PreflightPriority } from '../../Data/PreFlight/preflightTypes';
import styles from './ResourceFlowGraph.module.css';

/* ------------------------------------------------------------------ */
/*  Config                                                             */
/* ------------------------------------------------------------------ */

const STATE_FILL: Record<PreflightState, string> = {
  draft: '#6b7280',
  seeking: '#3b82f6',
  blocked: '#ef4444',
  partial: '#f59e0b',
  met: '#22c55e',
  withdrawn: '#9ca3af',
};

const PRIORITY_RADIUS: Record<PreflightPriority, number> = {
  critical: 22,
  important: 17,
  nice_to_have: 13,
};

const LAYER_HEIGHT = 80;
const NODE_SPACING = 90;
const PADDING_X = 60;
const PADDING_Y = 50;

/* ------------------------------------------------------------------ */
/*  Graph layout types                                                 */
/* ------------------------------------------------------------------ */

interface NodeLayout {
  item: PreflightItem;
  x: number;
  y: number;
  r: number;
}

interface Edge {
  from: string; // item_guid
  to: string;   // item_guid
}

/* ------------------------------------------------------------------ */
/*  Topological sort into layers                                       */
/* ------------------------------------------------------------------ */

function topoLayers(items: PreflightItem[]): PreflightItem[][] {
  const guidMap = new Map(items.map(i => [i.item_guid.toUpperCase(), i]));
  const inDegree = new Map<string, number>();
  const adj = new Map<string, string[]>(); // parent → children

  for (const item of items) {
    const key = item.item_guid.toUpperCase();
    if (!inDegree.has(key)) inDegree.set(key, 0);
    if (!adj.has(key)) adj.set(key, []);
  }

  // Build adjacency: if B depends_on A, then A → B (A blocks B)
  for (const item of items) {
    const childKey = item.item_guid.toUpperCase();
    for (const dep of item.depends_on ?? []) {
      const parentKey = dep.toUpperCase();
      if (!guidMap.has(parentKey)) continue;
      adj.get(parentKey)?.push(childKey);
      inDegree.set(childKey, (inDegree.get(childKey) ?? 0) + 1);
    }
  }

  const layers: PreflightItem[][] = [];
  let frontier = [...inDegree.entries()].filter(([, d]) => d === 0).map(([k]) => k);

  while (frontier.length > 0) {
    const layer = frontier.map(k => guidMap.get(k)!).filter(Boolean);
    layers.push(layer);
    const nextFrontier: string[] = [];
    for (const k of frontier) {
      for (const child of adj.get(k) ?? []) {
        const newDeg = (inDegree.get(child) ?? 1) - 1;
        inDegree.set(child, newDeg);
        if (newDeg === 0) nextFrontier.push(child);
      }
    }
    frontier = nextFrontier;
  }

  return layers;
}

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ResourceFlowGraphProps {
  items: PreflightItem[];
  onSelectItem?: (item: PreflightItem) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ResourceFlowGraph({ items, onSelectItem }: ResourceFlowGraphProps) {
  const { t } = useAppTranslation('kpi');
  const [hoveredGuid, setHoveredGuid] = useState<string | null>(null);

  // Filter to items with dependencies or that ARE dependencies
  const relevantItems = useMemo(() => {
    const hasDeps = new Set<string>();
    for (const item of items) {
      if (item.state === 'withdrawn') continue;
      if (item.depends_on?.length) {
        hasDeps.add(item.item_guid.toUpperCase());
        for (const d of item.depends_on) hasDeps.add(d.toUpperCase());
      }
    }
    return items.filter(i => i.state !== 'withdrawn' && hasDeps.has(i.item_guid.toUpperCase()));
  }, [items]);

  // Layout
  const { nodes, edges, width, height } = useMemo(() => {
    if (relevantItems.length === 0) {
      return { nodes: [] as NodeLayout[], edges: [] as Edge[], width: 200, height: 100 };
    }

    const layers = topoLayers(relevantItems);
    const nodeMap = new Map<string, NodeLayout>();
    let maxLayerWidth = 0;

    for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
      const layer = layers[layerIdx];
      maxLayerWidth = Math.max(maxLayerWidth, layer.length);
      for (let nodeIdx = 0; nodeIdx < layer.length; nodeIdx++) {
        const item = layer[nodeIdx];
        const r = PRIORITY_RADIUS[item.priority] || 17;
        nodeMap.set(item.item_guid.toUpperCase(), {
          item,
          x: PADDING_X + nodeIdx * NODE_SPACING + NODE_SPACING / 2,
          y: PADDING_Y + layerIdx * LAYER_HEIGHT + LAYER_HEIGHT / 2,
          r,
        });
      }
    }

    const edgeList: Edge[] = [];
    for (const item of relevantItems) {
      for (const dep of item.depends_on ?? []) {
        if (nodeMap.has(dep.toUpperCase())) {
          edgeList.push({ from: dep.toUpperCase(), to: item.item_guid.toUpperCase() });
        }
      }
    }

    const w = Math.max(maxLayerWidth * NODE_SPACING + PADDING_X * 2, 300);
    const h = layers.length * LAYER_HEIGHT + PADDING_Y * 2;

    return { nodes: Array.from(nodeMap.values()), edges: edgeList, width: w, height: h };
  }, [relevantItems]);

  const handleNodeClick = useCallback((item: PreflightItem) => {
    onSelectItem?.(item);
  }, [onSelectItem]);

  if (relevantItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <span className={styles.emptyIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 12h8" />
          </svg>
        </span>
        <span className={styles.emptyText}>{t('preflight.noDependencies')}</span>
      </div>
    );
  }

  const nodeMap = new Map(nodes.map(n => [n.item.item_guid.toUpperCase(), n]));

  return (
    <div className={styles.graphContainer}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={styles.graphSvg}
      >
        {/* Marker for arrowheads */}
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 6"
            refX="10"
            refY="3"
            markerWidth="8"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 3 L 0 6 z" fill="var(--text-muted)" />
          </marker>
          <marker
            id="arrow-active"
            viewBox="0 0 10 6"
            refX="10"
            refY="3"
            markerWidth="8"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 3 L 0 6 z" fill="var(--info-text)" />
          </marker>
        </defs>

        {/* Edges */}
        {edges.map((edge, idx) => {
          const fromNode = nodeMap.get(edge.from);
          const toNode = nodeMap.get(edge.to);
          if (!fromNode || !toNode) return null;

          const isActive = hoveredGuid &&
            (edge.from === hoveredGuid.toUpperCase() || edge.to === hoveredGuid.toUpperCase());

          // Simple straight line from bottom of source to top of target
          const x1 = fromNode.x;
          const y1 = fromNode.y + fromNode.r;
          const x2 = toNode.x;
          const y2 = toNode.y - toNode.r;

          return (
            <line
              key={idx}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={isActive ? 'var(--info-text)' : 'var(--text-muted)'}
              strokeWidth={isActive ? 2 : 1}
              strokeOpacity={isActive ? 1 : 0.4}
              markerEnd={isActive ? 'url(#arrow-active)' : 'url(#arrow)'}
              style={{ transition: 'stroke 0.15s, stroke-width 0.15s, stroke-opacity 0.15s' }}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map(node => {
          const isHovered = hoveredGuid?.toUpperCase() === node.item.item_guid.toUpperCase();
          return (
            <g
              key={node.item.item_guid}
              className={styles.node}
              onClick={() => handleNodeClick(node.item)}
              onMouseEnter={() => setHoveredGuid(node.item.item_guid)}
              onMouseLeave={() => setHoveredGuid(null)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={node.x}
                cy={node.y}
                r={node.r}
                fill={STATE_FILL[node.item.state] || '#6b7280'}
                fillOpacity={0.2}
                stroke={STATE_FILL[node.item.state] || '#6b7280'}
                strokeWidth={isHovered ? 2.5 : 1.5}
                style={{ transition: 'stroke-width 0.15s' }}
              />
              <text
                x={node.x}
                y={node.y}
                dominantBaseline="central"
                textAnchor="middle"
                className={styles.nodeLabel}
                fontSize={node.r < 15 ? 8 : 10}
              >
                {node.item.label.length > 12
                  ? node.item.label.slice(0, 11) + '\u2026'
                  : node.item.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip on hover */}
      {hoveredGuid && (() => {
        const node = nodeMap.get(hoveredGuid.toUpperCase());
        if (!node) return null;
        return (
          <div
            className={styles.tooltip}
            style={{
              insetInlineStart: node.x + node.r + 12,
              insetBlockStart: node.y - 10,
            }}
          >
            <strong>{node.item.label}</strong>
            <span className={styles.tooltipState}>
              {t(`preflight.states.${node.item.state}`)} · {t(`preflight.priorities.${node.item.priority}`)}
            </span>
            {node.item.description && (
              <span className={styles.tooltipDesc}>{node.item.description}</span>
            )}
          </div>
        );
      })()}
    </div>
  );
}
